import {
  Active,
  DndContext,
  DragEndEvent,
  Over,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormEvent, useState } from 'react';
import { CategoriesOrder } from '../../types/categories-order';
import { CategoriesWithRatingsByYearSegment } from '../../types/categories-with-ratings-by-year-segment';
import { Category, YearSegment } from '../../types/category';
import DraggableCategory from './DraggableCategory';

const CategoriesReorderDialog: React.FC<{
  onClose: () => void;
  onConfirm: (categoriesOrder: CategoriesOrder) => void;
  categoriesWithRatings: CategoriesWithRatingsByYearSegment;
}> = ({ onClose, onConfirm, categoriesWithRatings }) => {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

  const [categoriesWholeYear, setCategoriesWholeYear] = useState<Category[]>(
    categoriesWithRatings.wholeYear.map((cr) => cr.category)
  );
  const [categoriesFirstHalf, setCategoriesFirstHalf] = useState<Category[]>(
    categoriesWithRatings.firstHalf.map((cr) => cr.category)
  );
  const [categoriesSecondHalf, setCategoriesSecondHalf] = useState<Category[]>(
    categoriesWithRatings.secondHalf.map((cr) => cr.category)
  );

  const showWholeYearDivider = (): boolean =>
    categoriesWholeYear.length > 0 &&
    (categoriesFirstHalf.length > 0 || categoriesSecondHalf.length > 0);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (categoriesWholeYear.some((c) => c.id === active.id)) {
      moveCategoryByYearSegment(active, over, YearSegment.WholeYear);
    } else if (categoriesFirstHalf.some((c) => c.id === active.id)) {
      moveCategoryByYearSegment(active, over, YearSegment.FirstHalf);
    } else if (categoriesSecondHalf.some((c) => c.id === active.id)) {
      moveCategoryByYearSegment(active, over, YearSegment.SecondHalf);
    }
  };

  const moveCategoryByYearSegment = (
    active: Active,
    over: Over,
    yearSegment: YearSegment
  ) => {
    const moveCategory = (oldCategories: Category[]) => {
      const oldIndex = oldCategories.findIndex((c) => c.id === active.id);
      if (oldIndex !== -1) {
        const newIndex = oldCategories.findIndex((c) => c.id === over.id);
        return arrayMove(oldCategories, oldIndex, newIndex);
      }
      return oldCategories;
    };

    switch (yearSegment) {
      case YearSegment.WholeYear:
        setCategoriesWholeYear(moveCategory);
        break;
      case YearSegment.FirstHalf:
        setCategoriesFirstHalf(moveCategory);
        break;
      case YearSegment.SecondHalf:
        setCategoriesSecondHalf(moveCategory);
        break;
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    onConfirm({
      categoryIdsWholeYear: categoriesWholeYear.map((c) => c.id!.toString()),
      categoryIdsFirstHalf: categoriesFirstHalf.map((c) => c.id!.toString()),
      categoryIdsSecondHalf: categoriesSecondHalf.map((c) => c.id!.toString()),
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      disableRestoreFocus
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>Upravit pořadí kategorií</DialogTitle>
      <DialogContent>
        <DndContext
          modifiers={[restrictToParentElement]}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {showWholeYearDivider() && (
            <Typography variant="h6">Celý rok</Typography>
          )}
          <SortableContext
            items={[
              ...categoriesWholeYear.map((c) => c.id!.toString()),
              ...categoriesFirstHalf.map((c) => c.id!.toString()),
              ...categoriesSecondHalf.map((c) => c.id!.toString()),
            ]}
          >
            <Box>
              {categoriesWholeYear.map((category, index) => (
                <DraggableCategory
                  key={category.id}
                  category={category}
                  index={index}
                />
              ))}
            </Box>
            {categoriesWithRatings.firstHalf.length > 0 && (
              <Typography variant="h6">1. pololetí</Typography>
            )}
            <Box>
              {categoriesFirstHalf.map((category, index) => (
                <DraggableCategory
                  key={category.id}
                  category={category}
                  index={index}
                />
              ))}
            </Box>
            {categoriesWithRatings.secondHalf.length > 0 && (
              <Typography variant="h6">2. pololetí</Typography>
            )}
            <Box>
              {categoriesSecondHalf.map((category, index) => (
                <DraggableCategory
                  key={category.id}
                  category={category}
                  index={index}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zrušit</Button>
        <Button variant="contained" type="submit">
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoriesReorderDialog;
